import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Bell, Check, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Sprint 29.1 — In-app notification bell.
 * Owners / managers see live staff activity. Polls every 45s.
 */

function timeShort(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  } catch { return ""; }
}

const OWNER_ROLES = new Set(["owner", "restaurant_owner", "manager", "restaurant_manager"]);

export default function NotificationBell({ user }) {
  const { lang } = useI18n();
  const de = lang === "de";
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const canSee = !!user && (OWNER_ROLES.has(user.role) || user.is_super_admin);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data?.items || []);
      setUnread(data?.unread || 0);
    } catch { /* ignore — probably restricted */ }
  };

  useEffect(() => {
    if (!canSee) return;
    load();
    const t = setInterval(load, 45_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSee]);

  const markOne = async (id) => {
    setItems(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnread(u => Math.max(0, u - 1));
    try { await api.post("/notifications/mark-read", { id }); } catch { /* noop */ }
  };

  const markAll = async () => {
    setItems(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnread(0);
    try { await api.post("/notifications/mark-all-read"); } catch { /* noop */ }
  };

  if (!canSee) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost" size="icon"
          className="relative h-9 w-9 rounded-lg"
          data-testid="notif-bell"
          title={de ? "Benachrichtigungen" : "Bildirimler"}
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span
              data-testid="notif-unread-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center px-1 leading-none animate-pulse"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 rounded-xl p-0" data-testid="notif-panel">
        <div className="p-3 border-b flex items-center justify-between">
          <div className="text-sm font-semibold">
            {de ? "Benachrichtigungen" : "Bildirimler"}
          </div>
          {items.some(n => !n.is_read) && (
            <Button
              variant="ghost" size="sm"
              onClick={markAll}
              className="h-7 text-xs rounded-md"
              data-testid="notif-mark-all-read"
            >
              <CheckCheck className="w-3.5 h-3.5 mr-1" />
              {de ? "Alle gelesen" : "Tümü okundu"}
            </Button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto divide-y">
          {items.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="notif-empty">
              {de ? "Noch keine Aktivität heute." : "Bugün henüz etkinlik yok."}
            </div>
          )}
          {items.map((n) => (
            <div
              key={n.id}
              data-testid={`notif-item-${n.id}`}
              className={`p-3 flex items-start gap-3 ${n.is_read ? "" : "bg-primary/5"}`}
            >
              <div className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${n.is_read ? "bg-transparent" : "bg-primary"}`} />
              <div className="min-w-0 flex-1">
                <div className="text-sm leading-snug">
                  {n.title}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1 font-mono flex items-center gap-2">
                  <span>{timeShort(n.created_at)}</span>
                  {n.actor_role && (
                    <>
                      <span className="opacity-40">·</span>
                      <span className="uppercase tracking-wider">{n.actor_role}</span>
                    </>
                  )}
                </div>
              </div>
              {!n.is_read && (
                <Button
                  variant="ghost" size="icon"
                  onClick={() => markOne(n.id)}
                  className="h-6 w-6 rounded-md shrink-0"
                  title={de ? "Als gelesen markieren" : "Okundu olarak işaretle"}
                  data-testid={`notif-read-${n.id}`}
                >
                  <Check className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
